import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';

import {
  AQUAMARINE,
  AQUAMARINE_SOFT,
  BORDER_GRAY,
  WARNING,
  WARNING_SOFT,
} from '../../../theme';
import { REVIEW_STEP_ID } from './launchState';
import type { LaunchStep, StepStatus } from './types';

const Badge: React.FC<{ done: boolean; children: React.ReactNode }> = ({
  done,
  children,
}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      mt: 0.5,
      mr: 0.5,
      px: 0.75,
      py: 0.25,
      borderRadius: 0.5,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: done ? 'secondary.main' : WARNING,
      bgcolor: done ? AQUAMARINE_SOFT : WARNING_SOFT,
    }}
  >
    {children}
  </Box>
);

type Props = {
  steps: LaunchStep[];
  statuses: StepStatus[];
  currentIndex: number;
  readOnly: boolean;
  onSelect: (index: number) => void;
};

export const StepList: React.FC<Props> = ({
  steps,
  statuses,
  currentIndex,
  readOnly,
  onSelect,
}) => (
  <Stack component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
    {steps.map((step, index) => {
      const status = statuses[index];
      const isCurrent = index === currentIndex;
      const done = !isCurrent && status.complete && status.reachable;
      const disabled = !readOnly && !status.reachable;
      return (
        <li key={step.id}>
          <ButtonBase
            onClick={() => onSelect(index)}
            disabled={disabled}
            aria-current={isCurrent ? 'step' : undefined}
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '28px 1fr',
              gap: 1.25,
              alignItems: 'start',
              textAlign: 'left',
              p: 1,
              borderRadius: 2,
              bgcolor: isCurrent ? 'background.paper' : undefined,
              boxShadow: isCurrent ? `0 0 0 1px ${BORDER_GRAY}` : undefined,
              '&:hover': { bgcolor: 'background.paper' },
              opacity: disabled ? 0.55 : 1,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 600,
                border: `2px solid ${done ? AQUAMARINE : BORDER_GRAY}`,
                bgcolor: done ? AQUAMARINE : undefined,
              }}
            >
              {done ? '✓' : index + 1}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {step.title}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {step.subtitle}
              </Typography>
              <Box>
                {status.itemCount > 0 && (
                  <Badge done={done}>
                    {done ? '✓ Confirmed' : 'Checkpoint'}
                  </Badge>
                )}
                {step.id !== REVIEW_STEP_ID && status.saved && (
                  <Badge done={!status.dirty}>
                    {status.dirty ? 'Unsaved changes' : 'Saved'}
                  </Badge>
                )}
              </Box>
            </Box>
          </ButtonBase>
        </li>
      );
    })}
  </Stack>
);
